import { compact, keyBy, uniq } from 'lodash'
import { getUnitsInAnswers } from '../../../shared/domain/reports/answers.no-deps'
import type {
    ActivityGroup,
    FormGroup,
    HistoricalReport,
    NodeGroup,
    UnitGroup,
    ViewablePageReport,
    ViewableReportData,
} from '../../../shared/domain/viewableReports/types.no-deps'
import {
    type CompactReportGetOutput,
    isAnswerValueOfType,
    isLegacyReport,
    type ReportGetOutput,
} from '../../../shared/domain/reports/types.no-deps'
import { Translations } from '../../../shared/i18n/Translations'
import { filter, flatten, map } from '../../../shared/lib/functional/array.no-deps'
import type { Dictionary } from '../../../shared/lib/functional/dictionary.no-deps'
import { pipe } from '../../../shared/lib/functional/pipe.no-deps'
import type { Nullable } from '../../../shared/lib/typeHelpers.no-deps'
import { Guid_fetchSyncGuidesByIds } from '../Guid/Guid.helpers'
import { I18N_replaceNameSlugs, I18N_replaceSlug } from '../I18N/I18N.slugs'
import { IDB_actionTypeRead } from '../IDB/IDB.table.actionType'
import { Logg_createLogger } from '../Logg/Logg.Logger'
import { Serv_getReport } from '../Serv/Serv.reports'
import { Unit_fetchById, Unit_fetchByIds, UnitType_fetchById } from '../Unit/Unit.fetch'
import { WF_fetch } from '../WF/WF.fetch'
import { fromLoopPath } from '../../../shared/domain/flows/LoopPath.no-deps'
import type * as Guide from '../../../shared/domain/guides'
import { type Workflow, isAppActivityWorkflow } from '../../../app-core/lib/workflow/Workflow'
import { WF_ensureVersionOnDisk } from '../WF/WF.WorkflowReference'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import type { WorkflowVersionIdentifier } from '../../../shared/domain/workflows'
import type { SupportedLanguage } from '../../../shared/i18n/types.no-deps'
import { answerToViewable, buildFlowGroups } from '../../../shared/domain/viewableReports/viewablePageReport.no-deps'
import { isNil } from '../../../shared/lib/functional/nil.no-deps'

export async function Rpts_fetchWorkflow(
    logger: ClientLogger,
    workflow: WorkflowVersionIdentifier,
    language?: SupportedLanguage
): Promise<Workflow> {
    // The workflow is only fetched from the server when it needs to be translated.
    if (language) {
        return Base_commonDeps()
            .httpClient.getWorkflow(workflow.id, {
                workflowVersion: workflow.version,
                includeDeleted: true,
                language,
            })
            .then((response) => ({ ...response.workflow, name: response.workflow.name.text, procedure: null }))
    }

    return WF_fetch(logger, workflow.id, { includeDeleted: true })
}

export async function Rpts_fetchReportData(id: number, language?: string): Promise<Nullable<CompactReportGetOutput>> {
    // Fetch the report from server
    const report: Nullable<ReportGetOutput> = await Serv_getReport(id, false, true, language)

    return {
        id: report.id,
        title: report.title,
        clientId: report.clientId,
        answers: report.answers,
        workflow: report.workflow,
        workflowVersion: report.workflowVersion,
        subjectUnitId: report.subjectUnitId,
        reportDetails: report.reportDetails,
        photoUrls: report.photoUrls,
        originId: report.originId,
        certificationId: report.certificationId,
        ancestorReports: report.ancestorReports,
        language: report.language,
        flowSummary: report.flowSummary,
        cachedUnitData: report.cachedUnitData,
    }
}

export async function Rpts_viewReportFetch(
    report: CompactReportGetOutput,
    workflowData: Nullable<Workflow>,
    language?: SupportedLanguage
): Promise<ViewablePageReport> {
    const logger = Logg_createLogger('Rpts_viewReportFetch')

    const isLegacy = isLegacyReport(report)
    const reportAnswers = report.answers || []
    const workflow =
        workflowData ??
        (await Rpts_fetchWorkflow(logger, { id: report.workflow, version: report.workflowVersion }, language))

    // Infer title from the report's title or workflow name, default to the "Report" word.
    const title = report.title || workflow?.name || Translations.t('app:Rpts.View.title')

    // Get all relevant activities and units
    const activityAnswers = reportAnswers.filter((a) => a.activityIds != null)
    const unitAnswers = activityAnswers.filter((a) => a.activityIds.unit != null)
    const activityIds = uniq(activityAnswers.map((a) => a.activityIds.activity))
    const unitIds = uniq(unitAnswers.map((a) => a.activityIds.unit.id))
    const [activities, units] = await Promise.all([
        Promise.all(activityIds.map(IDB_actionTypeRead)).then(compact),
        Unit_fetchByIds(unitIds),
    ])

    // Get the units for all the answers
    const unitsFromUnitAnswers = getUnitsInAnswers(reportAnswers, report.reportDetails)
    const unitsInAnswers = await Unit_fetchByIds(unitsFromUnitAnswers)
    const unitTypeIds: number[] = uniq([
        ...reportAnswers.filter((answer) => answer.unitTypeIds).flatMap((answer) => answer.unitTypeIds ?? []),
        ...(unitsInAnswers.length > 0 ? unitsInAnswers.map((unit) => unit.type) : []),
    ])

    const unitTypesInAnswers = await Promise.all(unitTypeIds.map((unitTypeId) => UnitType_fetchById(unitTypeId))).then(
        I18N_replaceNameSlugs
    )

    // Get the unit subject for the report
    const subjectUnitId = report.subjectUnitId
    const subjectUnit = subjectUnitId != null ? await Unit_fetchById(logger, subjectUnitId) : null

    // Find units looped over
    const loopUnitIds = pipe(
        reportAnswers,
        filter((a) => a.loopPath != null && a.loopPath !== ''),
        map((a) => fromLoopPath(a.loopPath)),
        flatten,
        map((loopId) => loopId.unitId),
        compact,
        uniq
    )
    const loopUnits = await Unit_fetchByIds(loopUnitIds)

    // Get  guide ids from guide answers
    const guideIds: Guide.Id[] = pipe(
        reportAnswers,
        map((answer) => {
            if (isAnswerValueOfType('guide', answer.value)) {
                const value = answer.value.value
                return Array.isArray(value) ? value.map((v) => v.guideId) : value.guideId
            }
        }),
        compact,
        flatten
    )

    const guides = await Guid_fetchSyncGuidesByIds(logger, guideIds)
    const guideLookup: Dictionary<Guide.SyncGuide> = keyBy(guides, 'id')

    // Group all the answers
    const viewReport: ViewablePageReport = {
        ...(report.originId && { originId: report.originId }),
        title,
        isLegacy,
        unitsInAnswers,
        unitTypesInAnswers,
        activityGroups: [],
        flowGroups: [],
        answers: [],
        subjectUnit,
        reportDetails: report.reportDetails,
        photoUrls: report.photoUrls,
        ancestorReports: report.ancestorReports,
        guideLookup,
        flow: null,
        language: report.language,
        workflow: { id: report.workflow, version: report.workflowVersion },
        cachedUnitData: report.cachedUnitData,
    }

    for (const answer of reportAnswers) {
        let activityGroup: ActivityGroup
        let unitGroup: UnitGroup
        let nodeGroup: NodeGroup
        let formGroup: FormGroup

        if (answer.activityIds != null) {
            const activityId = answer.activityIds.activity

            // Find existing activity group
            activityGroup = viewReport.activityGroups.find((ag) => ag.id === activityId)

            // Otherwise
            if (activityGroup == null) {
                // Find the activity
                const activity = activities.find((a) => a.id === activityId)

                // If the activity is missing, will end up adding the answer to the top level below
                if (activity != null) {
                    // Create new activity group
                    activityGroup = {
                        id: activity.id,
                        name: I18N_replaceSlug(activity.name),
                        color: activity.display.color,
                        logo: activity.display.logo,
                        unitGroups: [],
                        nodeGroups: [],
                        formGroups: [],
                        answers: [],
                    }

                    // Add to the view report
                    viewReport.activityGroups.push(activityGroup)
                }
            }

            // If this is a answer for a unit
            if (answer.activityIds.unit != null) {
                const { id: unitId, instance } = answer.activityIds.unit

                // Find existing unit group
                unitGroup = activityGroup.unitGroups.find((ug) => ug.instance === instance)

                // Otherwise
                if (unitGroup == null) {
                    // Find the unit
                    const unit = units.find((u) => u.id === unitId)

                    // If the unit is missing, will end up adding to activity directly
                    if (unit != null) {
                        // Create new unit group
                        unitGroup = {
                            instance,
                            name: I18N_replaceSlug(unit.name),
                            image: unit.data.image,
                            nodeGroups: [],
                            formGroups: [],
                            answers: [],
                        }

                        // Add to the activity
                        activityGroup.unitGroups.push(unitGroup)
                    }
                }
            }

            // If this is an answer for a process flow
            if (answer.activityIds.node != null) {
                // Identify each cycle for node group
                const { cycle } = answer.activityIds.node

                // Parent grouping is either:
                // Unit (unit process flow)
                // Activity (location process flow)
                const parent = unitGroup || activityGroup

                // Find the node group from the parent
                nodeGroup = parent.nodeGroups.find((ng) => ng.cycle === cycle)

                if (nodeGroup == null) {
                    // Or create a new node group
                    nodeGroup = { cycle, formGroups: [], answers: [] }

                    // And add it to the parent
                    parent.nodeGroups.push(nodeGroup)
                }
            }

            // If this is an answer for a form
            if (answer.activityIds.form != null) {
                const { form } = answer.activityIds

                // Parent grouping could be:
                // Node (Form process flow node)
                // Unit (Unit form)
                // Activity (Location Form)
                const parent = nodeGroup || unitGroup || activityGroup

                // Find the form group from the parent
                formGroup = parent.formGroups.find((fg) => fg.id === form.id)

                if (formGroup == null) {
                    // Or create a new form group
                    formGroup = { id: form.id, answers: [] }

                    // And add it to the parent
                    parent.formGroups.push(formGroup)
                }
            }
        }

        // Find the right place to put the answer
        const answers =
            (formGroup && formGroup.answers) ||
            (nodeGroup && nodeGroup.answers) ||
            (unitGroup && unitGroup.answers) ||
            (activityGroup && activityGroup.answers) ||
            (isNil(answer.loopPath) && viewReport.answers) ||
            null

        if (isNil(answers)) {
            continue
        }

        // Push the answer in
        answers.push(answerToViewable(answer, report.photoUrls))
    }

    let flow = null

    if (!isAppActivityWorkflow(workflow)) {
        if (report.workflowVersion && report.workflowVersion !== workflow.numberOfVersions && !language) {
            const logger = Logg_createLogger(`Rpts_viewReportFetch`)
            const workflowVersion = await WF_ensureVersionOnDisk(logger, {
                id: report.workflow,
                version: report.workflowVersion,
            })
            flow = workflowVersion.flow
        } else {
            flow = workflow.flow
        }
    }

    viewReport.flowGroups = buildFlowGroups(
        report.flowSummary?.elements,
        report.photoUrls,
        reportAnswers,
        flow,
        loopUnits.map((unit) => ({
            id: unit.id,
            name: I18N_replaceSlug(unit.name),
            image: unit.data.image,
        })),
        workflow.displaysTextContentInReports
    )

    viewReport.flow = flow

    return viewReport
}

export const Rpts_fetchViewableReportData = async (
    logger: ClientLogger,
    reportId: number,
    workflow?: WorkflowVersionIdentifier,
    language?: SupportedLanguage
): Promise<Nullable<ViewableReportData>> => {
    const [reportData, workflowData] = await Promise.all([
        Rpts_fetchReportData(reportId, language).catch((error) => {
            if (error?.status === 400) {
                return null // Upstream component expects a null value if the report couldn't be found.
            }

            logger.warn('Could not fetch report to view', { reportId, error })

            throw error
        }),
        workflow ? Rpts_fetchWorkflow(logger, workflow, language) : null,
    ])

    if (!reportData) return null

    const viewablePageReport = await Rpts_viewReportFetch(reportData, workflowData, language)

    return {
        ...viewablePageReport,
        reportId: reportData.id,
        reportClientId: reportData.clientId,
    }
}

export const Rpts_fetchTranslatedHistoricalReport = async (
    historicalReport: HistoricalReport,
    translatedReports: Map<number, HistoricalReport>,
    userLanguage: SupportedLanguage
): Promise<HistoricalReport> => {
    const { report } = historicalReport

    const cachedTranslation = translatedReports.get(report.id)
    if (cachedTranslation) {
        return cachedTranslation
    }

    const maybeGetAction = report.scheduledInstance
        ? Base_commonDeps()
              .httpClient.getActions(report.scheduledInstance, userLanguage)
              .then(({ actions }) => actions[0])
        : null

    const logger = Logg_createLogger('Rpts_fetchTranslatedHistoricalReport')
    const [viewableReportData, translatedAction] = await Promise.all([
        Rpts_fetchViewableReportData(
            logger,
            report.id,
            { id: report.workflow, version: report.workflowVersion },
            userLanguage
        ),
        maybeGetAction,
    ])

    const translated: HistoricalReport = {
        ...historicalReport,
        reportViewData: { ...historicalReport.reportViewData, ...viewableReportData },

        // Translates the action's description text.
        ...(translatedAction && {
            originInfo: {
                ...historicalReport.originInfo,
                description: translatedAction.description,
            },
        }),
    }

    translatedReports.set(report.id, translated)

    return translated
}
